import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { contentService, QuizFlowItemDto, PublicQuickLaunchCardDto } from '../services/contentService';
import { useNotifications } from './feedback/NotificationSystem';
import { PersianOverlayLoader } from './shared/PersianLoader';
import { ResponsiveContainer } from './shared/MobileResponsive';
import { CatalogFlowFilterCollapsible } from './shared/CatalogFlowFilterCollapsible';
import { CatalogFlowQuizTrophyIcon, CatalogFlowTrophyBadge } from './shared/CatalogFlowTrophyBadge';
import { CatalogFlowPageHeader } from './shared/CatalogFlowPageHeader';
import { CatalogFlowHeaderProgressBar } from './shared/CatalogFlowHeaderProgressBar';
import { SimplePageHeader } from './shared/SimplePageHeader';
import { useLearnerHomeDesign } from '../utils/learnerHomeDesign';
import {
  catalogFlowActionBtnIconClass,
  catalogFlowActionBtnRowClass,
  catalogFlowActionBtnSizeClass,
} from './shared/catalogFlowItemStyles';
import { LessonMapPanel } from './learning/LessonMapPanel';
import { Trophy, ExternalLink } from 'lucide-react';
import { QuizModal, QuizType } from './learning/QuizModal';
import { NonFarsiContentHintModal } from './learning/NonFarsiContentHintModal';
import { saveQuizFlowPosition, quizFlowItemElementId } from '../utils/lastLearningPosition';
import { buildCatalogFlowCourseOptions } from '../utils/catalogFlowCourse';
import { buildCatalogFlowMap } from '../utils/catalogFlowMapSections';
import type { LessonMapItem } from '../utils/lessonMapSections';
import { FlowContextBreadcrumb } from './shared/FlowContextBreadcrumb';
import { useAppLocale } from '../providers/LocaleProvider';
import { useLocalizedContent } from '../hooks/useLocalizedContent';
import { useNonFarsiContentGate } from '../hooks/useNonFarsiContentGate';
import { isDesignedForNonFarsiUsers } from '../utils/nonFarsiContentHint';
import { pickLocalizedTitle } from '../utils/localizedContent';
import {
  readPersistedCatalogFlowFilters,
  writePersistedCatalogFlowFilters
} from '../utils/learnerFilterPersistence';

type CatalogFilterKey = 'all' | 'done' | 'remaining';

interface QuizFlowProps {
  onBack: () => void;
  onBackToDashboard?: () => void;
  onNavigateToLesson?: (lessonId: number) => void;
  /** From keep learning — flash and scroll to the last quiz row without opening the modal. */
  highlightItem?: { flowKind: string; quizId: number };
  onHighlightConsumed?: () => void;
}

function mapFlowKind(kind: string): QuizType {
  const k = (kind || '').toLowerCase();
  if (k === 'course') return 'course';
  if (k === 'lesson') return 'lesson';
  return 'level';
}

function viewLessonId(item: QuizFlowItemDto): number | null {
  const v = item.viewLessonId ?? item.lessonId;
  if (v == null || Number(v) <= 0) return null;
  return Number(v);
}

const PAGE_SIZE = 10;
const QUIZ_LAUNCH_DEFAULT_COLOR = '#8b5cf6';
const HIGHLIGHT_DURATION_MS = 5000;

export const QuizFlow: React.FC<QuizFlowProps> = ({
  onBack,
  onBackToDashboard,
  onNavigateToLesson,
  highlightItem,
  onHighlightConsumed
}) => {
  const { t } = useTranslation(['learning', 'common']);
  const { direction, locale } = useAppLocale();
  const { pickTitle: pickUiTitle } = useLocalizedContent();
  const isRtl = locale === 'fa' || direction === 'rtl';
  const [homeDesign] = useLearnerHomeDesign();
  const simpleHome = homeDesign === 'mme';
  const { showError } = useNotifications();
  const {
    gateState: nonFarsiGateState,
    gateClick: gateNonFarsiClick,
    openHintManually: openNonFarsiHintManually,
    handleDismiss: handleNonFarsiGateDismiss,
    handleContinue: handleNonFarsiGateContinue,
    isRtl: nonFarsiGateRtl,
  } = useNonFarsiContentGate();
  const pickItemTitle = (item: QuizFlowItemDto) =>
    isDesignedForNonFarsiUsers(item)
      ? pickLocalizedTitle(item, 'en')
      : pickUiTitle(item);
  const [items, setItems] = useState<QuizFlowItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeQuiz, setActiveQuiz] = useState<QuizFlowItemDto | null>(null);
  const [filter, setFilter] = useState<CatalogFilterKey>(() => readPersistedCatalogFlowFilters('quiz').filter);
  const [courseFilter, setCourseFilter] = useState<number | null>(
    () => readPersistedCatalogFlowFilters('quiz').courseFilter
  );
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [trophyLottieUnavailable, setTrophyLottieUnavailable] = useState(false);
  const [launchCard, setLaunchCard] = useState<PublicQuickLaunchCardDto | null>(null);
  const [activeHighlightKey, setActiveHighlightKey] = useState<string | null>(null);
  const highlightTimerRef = useRef<number | null>(null);
  const highlightHandledRef = useRef<string | null>(null);

  useEffect(() => {
    fetch('/animations/Trophy.lottie', { method: 'HEAD' })
      .then((r) => {
        if (!r.ok) setTrophyLottieUnavailable(true);
      })
      .catch(() => setTrophyLottieUnavailable(true));
  }, []);

  useEffect(() => {
    let cancelled = false;
    contentService
      .getPublicQuickLaunchCards()
      .then((cards) => {
        if (!cancelled) {
          setLaunchCard(cards.find((c) => c.cardKey === 'quiz') ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setLaunchCard(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const reload = useCallback(async () => {
    const list = await contentService.getQuizFlowItems();
    setItems(Array.isArray(list) ? list : []);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await reload();
      } catch (e) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : t('quizFlow.loadFailed'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reload]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initial load

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filter, courseFilter]);

  useEffect(() => {
    writePersistedCatalogFlowFilters('quiz', { filter, courseFilter });
  }, [filter, courseFilter]);

  const courseOptions = useMemo(() => buildCatalogFlowCourseOptions(items, locale), [items, locale]);

  const courseScopedItems = useMemo(() => {
    if (courseFilter == null) return items;
    return items.filter((i) => i.courseId === courseFilter);
  }, [items, courseFilter]);

  const filteredItems = useMemo(() => {
    if (filter === 'done') return courseScopedItems.filter((i) => i.isPassed);
    if (filter === 'remaining') return courseScopedItems.filter((i) => !i.isPassed);
    return courseScopedItems;
  }, [courseScopedItems, filter]);

  const passedCount = useMemo(() => courseScopedItems.filter((i) => i.isPassed).length, [courseScopedItems]);
  const remainingCount = useMemo(
    () => courseScopedItems.length - passedCount,
    [courseScopedItems, passedCount]
  );
  const totalPassedCount = useMemo(() => items.filter((i) => i.isPassed).length, [items]);

  useEffect(() => {
    if (loading) return;
    const summary =
      items.length > 0 ? t('quizFlow.positionSummary', { done: totalPassedCount, total: items.length }) : undefined;
    saveQuizFlowPosition(t('quizFlow.title'), summary, undefined, {
      touchUpdatedAt: false,
      progress: { done: totalPassedCount, total: items.length }
    });
  }, [loading, totalPassedCount, items.length]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid re-run when t changes on locale reload

  const positionSummary =
    items.length > 0 ? t('quizFlow.positionSummary', { done: totalPassedCount, total: items.length }) : undefined;

  const rememberQuizItem = useCallback(
    (item: QuizFlowItemDto) => {
      saveQuizFlowPosition(t('quizFlow.title'), positionSummary, {
        flowKind: item.kind,
        quizId: item.quizId,
        itemTitle: item.title,
        itemTitleFa: item.titleFa
      }, { progress: { done: totalPassedCount, total: items.length } });
    },
    [positionSummary, pickItemTitle, t]
  );

  useEffect(() => {
    if (!highlightItem || highlightItem.quizId <= 0 || loading) return;
    const target = items.find(
      (i) => i.quizId === highlightItem.quizId && i.kind === highlightItem.flowKind
    );
    if (!target) return;
    setFilter('all');
    setCourseFilter(target.courseId > 0 ? target.courseId : null);
  }, [highlightItem, loading, items]);

  useEffect(() => {
    if (!highlightItem || highlightItem.quizId <= 0) {
      highlightHandledRef.current = null;
      return;
    }
    if (loading) return;

    const requestKey = `${highlightItem.flowKind}-${highlightItem.quizId}`;
    if (highlightHandledRef.current === requestKey) return;

    const targetInAll = items.find(
      (i) => i.quizId === highlightItem.quizId && i.kind === highlightItem.flowKind
    );
    if (!targetInAll) {
      if (items.length > 0) {
        highlightHandledRef.current = requestKey;
        onHighlightConsumed?.();
      }
      return;
    }

    const targetIndex = filteredItems.findIndex(
      (i) => i.quizId === highlightItem.quizId && i.kind === highlightItem.flowKind
    );
    if (targetIndex < 0) return;

    if (targetIndex >= visibleCount) {
      setVisibleCount(targetIndex + 1);
      return;
    }

    highlightHandledRef.current = requestKey;
    const target = filteredItems[targetIndex];
    const highlightKey = `${target.kind}-${target.quizId}`;
    setActiveHighlightKey(highlightKey);

    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document
          .getElementById(quizFlowItemElementId(target.kind, target.quizId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    if (highlightTimerRef.current != null) {
      window.clearTimeout(highlightTimerRef.current);
    }
    highlightTimerRef.current = window.setTimeout(() => {
      setActiveHighlightKey(null);
      onHighlightConsumed?.();
      highlightTimerRef.current = null;
    }, HIGHLIGHT_DURATION_MS);

    return () => {
      cancelAnimationFrame(rafId);
      if (highlightTimerRef.current != null) {
        window.clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, [highlightItem, loading, items, filteredItems, visibleCount, onHighlightConsumed]);

  const launchAccentColor = launchCard?.color || QUIZ_LAUNCH_DEFAULT_COLOR;

  const pagedItems = useMemo(() => filteredItems.slice(0, visibleCount), [filteredItems, visibleCount]);

  const quizMapCurrentKey = activeQuiz
    ? `${activeQuiz.kind}-${activeQuiz.quizId}`
    : activeHighlightKey;

  const quizMapData = useMemo(
    () =>
      buildCatalogFlowMap(
        items.map((item) => ({
          kind: item.kind,
          entityId: item.quizId,
          courseId: item.courseId,
          levelId: item.levelId,
          lessonId: item.lessonId,
          title: item.title,
          titleFa: item.titleFa,
          contextSubtitle: item.contextSubtitle,
          contextSubtitleFa: item.contextSubtitleFa,
          isCompleted: item.isPassed
        })),
        {
          locale,
          currentKey: quizMapCurrentKey,
          courseFilter,
          mapTitle: t('quizMap.title')
        }
      ),
    [items, locale, quizMapCurrentKey, courseFilter, t]
  );

  const openQuiz = useCallback(
    (item: QuizFlowItemDto) => {
      rememberQuizItem(item);
      setActiveQuiz(item);
    },
    [rememberQuizItem]
  );

  const handleQuizMapNavigate = useCallback(
    (mapItem: LessonMapItem) => {
      const kind = mapItem.flowKind ?? 'lesson';
      const target = items.find((i) => i.quizId === mapItem.entityId && i.kind === kind);
      if (!target) return;

      setCourseFilter(target.courseId > 0 ? target.courseId : null);
      setFilter('all');

      const inCourse = items.filter((i) => i.courseId === target.courseId);
      const idx = inCourse.findIndex((i) => i.quizId === target.quizId && i.kind === target.kind);
      if (idx >= 0) setVisibleCount(Math.max(PAGE_SIZE, idx + 1));

      const highlightKey = `${target.kind}-${target.quizId}`;
      setActiveHighlightKey(highlightKey);
      if (highlightTimerRef.current != null) window.clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = window.setTimeout(() => {
        setActiveHighlightKey(null);
        highlightTimerRef.current = null;
      }, HIGHLIGHT_DURATION_MS);

      // Focus the grid row only — do not open the quiz.
      window.setTimeout(() => {
        document
          .getElementById(quizFlowItemElementId(target.kind, target.quizId))
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
    },
    [items]
  );

  const handleQuizClose = () => {
    setActiveQuiz(null);
    void reload();
  };

  if (loading) {
    return <PersianOverlayLoader isVisible message={t('quiz.loading')} />;
  }

  if (items.length === 0) {
    return (
      <ResponsiveContainer maxWidth="xl">
        <div className="learner-flow-page mme-tone py-12 text-center">
          <Trophy className="mx-auto mb-4 h-16 w-16 text-gray-400" />
          <p className="text-gray-600 dark:text-gray-400">{t('quizFlow.noQuizzesYet')}</p>
          <button type="button" onClick={onBack} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700">
            {t('common:back')}
          </button>
        </div>
      </ResponsiveContainer>
    );
  }

  return (
    <div className="learner-flow-page mme-tone min-h-screen bg-gray-50 pb-10 dark:bg-gray-950">
      <ResponsiveContainer maxWidth="xl" className="space-y-4 py-4 sm:py-6">
        {simpleHome ? (
          <>
            <SimplePageHeader tourId="quiz-flow-header" title={t('quizFlow.title')} onBack={onBack} />
            <CatalogFlowHeaderProgressBar done={totalPassedCount} total={items.length} accent="rose" variant="quiz" />
          </>
        ) : (
        <CatalogFlowPageHeader
          title={t('quizFlow.title')}
          subtitle={t('quizFlow.subtitle')}
          icon={Trophy}
          accentColor={launchAccentColor}
          guideAccent="rose"
          onBack={onBack}
          onBackToDashboard={onBackToDashboard ?? onBack}
          statsDone={totalPassedCount}
          statsTotal={items.length}
          progressVariant="quiz"
          tourId="quiz-flow-header"
        />
        )}

        <div data-tour-id="quiz-flow-filters">
        <CatalogFlowFilterCollapsible
          mode="quiz"
          filter={filter}
          onFilterChange={setFilter}
          countAll={courseScopedItems.length}
          countDone={passedCount}
          countRemaining={remainingCount}
          show={items.length > 0}
          collapsible={false}
          courseOptions={courseOptions}
          courseFilter={courseFilter}
          onCourseFilterChange={setCourseFilter}
        />
        </div>

        <div className="space-y-3">
          {filteredItems.length === 0 ? (
            <div className="rounded-2xl border border-rose-200/80 bg-white/90 p-6 text-center text-sm text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-900/90 dark:text-gray-400 dark:shadow-lg dark:shadow-black/35">
              {t('quizFlow.noQuizzesFilter')}
            </div>
          ) : (
            pagedItems.map((item, index) => {
              const key = `${item.kind}-${item.quizId}`;
              const isHighlighted = activeHighlightKey === key;
              const prevScore =
                item.bestScorePercent != null && !Number.isNaN(Number(item.bestScorePercent))
                  ? Number(item.bestScorePercent)
                  : undefined;
              const lessonNav = viewLessonId(item);
              return (
                <div
                  key={key}
                  id={quizFlowItemElementId(item.kind, item.quizId)}
                  className={`grid gap-x-3 gap-y-1 rounded-xl border p-4 shadow-sm transition-shadow dark:shadow-lg dark:shadow-black/35 ${
                    item.isPassed
                      ? 'grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center'
                      : 'grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center'
                  } ${isHighlighted ? 'search-result-highlight scroll-mt-24' : ''} ${
                    item.isPassed
                      ? 'border-green-200/80 bg-green-50 hover:bg-green-100 dark:border-emerald-500/45 dark:bg-emerald-900/55 dark:hover:bg-emerald-800/50'
                      : 'border-rose-200/70 bg-white/95 hover:border-rose-300/80 dark:border-gray-600 dark:bg-gray-900/95 dark:hover:border-gray-500'
                  }`}
                >
                  <p className="min-w-0 self-center font-medium leading-snug text-gray-900 dark:text-gray-100">
                    {pickItemTitle(item)}
                  </p>
                  {item.isPassed ? (
                    <CatalogFlowQuizTrophyIcon
                      lottieUnavailable={trophyLottieUnavailable}
                      className="col-start-2 row-start-1 self-center sm:row-span-4"
                    />
                  ) : null}
                  <div
                    className={`min-w-0 text-start ${
                      item.isPassed ? 'col-span-2 sm:col-span-1 sm:col-start-1' : ''
                    }`}
                  >
                    <FlowContextBreadcrumb
                      contextSubtitle={item.contextSubtitle}
                      contextSubtitleFa={item.contextSubtitleFa}
                      isRtl={isRtl}
                    />
                    {item.isPassed ? (
                      <CatalogFlowTrophyBadge variant="quiz-passed" lottieUnavailable={trophyLottieUnavailable} />
                    ) : (
                      <p className="mt-2 text-xs text-rose-800 dark:text-rose-300/90">{t('quizFlow.notPassedYet')}</p>
                    )}
                    {prevScore !== undefined && (
                      <p className="mt-1 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                        {t('quizFlow.bestScore', { score: prevScore })}
                      </p>
                    )}
                  </div>
                  <div
                    className={`${catalogFlowActionBtnRowClass} ${
                      item.isPassed
                        ? 'col-span-2 sm:col-span-1 sm:col-start-3 sm:row-start-1 sm:row-span-4 sm:self-center'
                        : 'sm:col-start-2 sm:row-start-1 sm:row-span-2 sm:self-center'
                    }`}
                  >
                    <button
                      type="button"
                      {...(index === 0 ? { 'data-tour-id': 'quiz-flow-take-quiz' } : {})}
                      onClick={(e) =>
                        gateNonFarsiClick(e, item, 'quiz', item.quizId, () => openQuiz(item))
                      }
                      className={`rounded-xl bg-rose-600 font-medium text-white shadow-sm transition-colors hover:bg-rose-700 dark:bg-rose-500 dark:hover:bg-rose-600 ${catalogFlowActionBtnSizeClass}`}
                    >
                      {item.isPassed ? t('quizFlow.takeQuizAgain') : t('quizFlow.takeQuiz')}
                    </button>
                    {onNavigateToLesson && lessonNav != null && (
                      <button
                        type="button"
                        {...(index === 0 ? { 'data-tour-id': 'quiz-flow-view-lesson' } : {})}
                        onClick={() => onNavigateToLesson(lessonNav)}
                        className={`flex items-center gap-1.5 rounded-xl bg-blue-600 font-medium text-white shadow-sm transition-colors hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 sm:gap-2 ${catalogFlowActionBtnSizeClass}`}
                      >
                        <ExternalLink className={catalogFlowActionBtnIconClass} />
                        {t('exerciseFlow.viewFullLesson')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {filteredItems.length > visibleCount && (
          <div className="flex justify-center pt-1">
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="rounded-xl border border-rose-300 bg-white px-5 py-2 text-sm font-medium text-rose-800 shadow-sm transition-colors hover:bg-rose-50 dark:border-rose-800 dark:bg-gray-900 dark:text-rose-200 dark:hover:bg-gray-800"
            >
              {t('exerciseFlow.moreLeft', { count: filteredItems.length - visibleCount })}
            </button>
          </div>
        )}
      </ResponsiveContainer>

      {activeQuiz && (
        <QuizModal
          key={`${activeQuiz.kind}-${activeQuiz.quizId}`}
          quizType={mapFlowKind(activeQuiz.kind)}
          quizId={activeQuiz.quizId}
          contentForceEnglish={isDesignedForNonFarsiUsers(activeQuiz)}
          {...(activeQuiz.bestScorePercent != null && !Number.isNaN(Number(activeQuiz.bestScorePercent))
            ? { previousScore: Number(activeQuiz.bestScorePercent) }
            : {})}
          onClose={handleQuizClose}
          {...(locale !== 'en' && isDesignedForNonFarsiUsers(activeQuiz)
            ? { onEnglishLockedHintClick: () => openNonFarsiHintManually('quiz', activeQuiz.quizId) }
            : {})}
        />
      )}

      <NonFarsiContentHintModal
        open={nonFarsiGateState.open}
        isRtl={nonFarsiGateRtl}
        anchorRect={nonFarsiGateState.anchorRect}
        contentKey={nonFarsiGateState.contentKey}
        onDismiss={handleNonFarsiGateDismiss}
        onContinue={handleNonFarsiGateContinue}
      />

      <LessonMapPanel
        mapData={quizMapData}
        onNavigate={handleQuizMapNavigate}
        persistOpenKey="quiz-flow-map-open"
        animateFocusChange
        labels={{
          title: t('quizMap.title'),
          openAria: t('quizMap.openAria'),
          closeAria: t('quizMap.closeAria'),
          bubbleTitle: t('quizMap.bubbleTitle'),
          navAria: t('quizMap.navAria'),
          empty: t('quizMap.empty')
        }}
      />
    </div>
  );
};
